import type { NavigationGuard } from "vue-router"
import { useAuthStore } from "@/store/authStore"
import { AuthRouteNameEnum } from "@/shared/enums/routes/auth.enum.ts"
import { UserRouteNameEnum } from "@/shared/enums/routes/user.enum.ts"
import { UserStatusEnum } from "@/shared/enums/userStatus.enum.ts"

export const authGuard: NavigationGuard = async (to, _from, next) => {
	const auth = useAuthStore()

	if (to.meta.authRequired && !auth.isAuthenticated) {
		return next({ name: AuthRouteNameEnum.login, query: { redirect: to.fullPath } })
	}

	if (auth.isAuthenticated && auth.permissions.length === 0) {
		await auth.syncFromServer()
	}

	if (auth.isAuthenticated && auth.user && to.meta.authRequired) {
		const status = String((auth.user as any).status ?? "")

		if (
			status === UserStatusEnum.pendingEmailVerification &&
			to.name !== AuthRouteNameEnum.mailConfirmation
		) {
			return next({ name: AuthRouteNameEnum.mailConfirmation })
		}
		if (status === UserStatusEnum.pendingDocuments && to.name !== UserRouteNameEnum.details) {
			return next({ name: UserRouteNameEnum.details })
		}

		// pending_moderation (under review), rejected (fix & resubmit) and
		// suspended (blocked) can't use the normal app — every write would 403.
		// Funnel them to the single status screen instead of letting them hit
		// silent failures across the campaign/place/screen flows.
		const onStatusScreen = to.name === UserRouteNameEnum.accountStatus
		if (
			(status === UserStatusEnum.pendingModeration ||
				status === UserStatusEnum.rejected ||
				status === UserStatusEnum.suspended ||
					status === UserStatusEnum.documentsExpired) &&
			!onStatusScreen
		) {
			return next({ name: UserRouteNameEnum.accountStatus })
		}
		// An approved/active user who lands on the status screen is sent home.
		if (status === UserStatusEnum.active && onStatusScreen) {
			return next({ name: "dashboard" })
		}
	}

	if (to.meta.permission && !auth.can(to.meta.permission)) {
		return next({ name: "forbidden" })
	}

	next()
}
